import React from 'react';
import { Container, Card, Row } from 'react-bootstrap';

import { FactoryIndex, Address, Factory } from 'impermax-sdk';
import { useWallet } from 'use-wallet';

import InteractionButton from '../components/InteractionButton';
import PairLeftTitle from '../components/PairLeftTitle';
import View from '../components/View';
import FactoryContext from '../contexts/Factory';
import PairAddressContext from '../contexts/PairAddress';
import useGenericCall from '../hooks/useGenericCall';
import useSubgraph from '../hooks/useOffchain';
import { useOnchainLendingPool } from '../hooks/useOnchain';
import useReinvest from '../hooks/useReinvest';
import { useGetWhitelistedPairs } from "../hooks/useNetwork";
import { formatFloat } from '../utils/format';

export function BountyRow() {
    const data = useGenericCall(useOnchainLendingPool(), async (r) => ({
        pendingReward: await r.getPendingReward(),
        reinvestBounty: await r.getReinvestBounty(),
    }));

    const [reinvestState, onReinvest] = useReinvest();

    if (!data) return null;

    return (
        <Row className="pairs-table-row">
            <div className="col col-7 col-md-6 col-lg-4 d-flex">
                <div className="currency-name">
                    <PairLeftTitle />
                </div>
            </div>
            <div className="col d-none d-md-flex col-4 col-lg-3 text-left align-items-center">
                <div>
                    {formatFloat(data.pendingReward.amount, 3)} {data.pendingReward.symbol}
                </div>
            </div>
            <div className="col d-none d-lg-flex col-3 text-left align-items-center">
                <div>
                    {formatFloat(data.reinvestBounty, 3)} {data.pendingReward.symbol}
                </div>
            </div>
            <div className="col col-5 col-md-2 d-flex align-items-center flex-row-reverse">
                <div className="interaction-button-container-small">
                    <InteractionButton name={'Reinvest'} onCall={onReinvest} state={reinvestState} />
                </div>
            </div>
        </Row>
    );
}

/**
 * Bounty page view.
 */
export default function Bounty() {
    const getWhitelistedPairs = useGetWhitelistedPairs();
    const data = useGenericCall(useSubgraph(), async (s) => {
        let stakedPairs = 0;
        const pairList = await s.getPairList();
        const stakedPairList: FactoryIndex<Address[]> = {};
        for (const pair of pairList) {
            if (getWhitelistedPairs(pair.factory).includes(pair.pairAddress)) {
                if (await (await s.getLendingPool(pair.factory, pair.pairAddress)).isStakedLPToken()) {
                    if (!stakedPairList[pair.factory]) stakedPairList[pair.factory] = [];
                    stakedPairList[pair.factory].push(pair.pairAddress);
                    stakedPairs++;
                }    
            }
        }
        return { stakedPairList, stakedPairs };
    });
    const { account } = useWallet();

    if (!account || !data) return <></>;

    return (
        <View>
            <Container className="">
                {data.stakedPairList && data.stakedPairs > 0 && (
                    <>
                        <Card className="pairs-table mt-4">
                            <div className="pairs-table-header row">
                                <div className="col col-7 col-md-6 col-lg-4">Market</div>
                                <div className="col d-none d-md-block col-2 col-lg-3 text-left">Pending Reward</div>
                                <div className="col d-none d-lg-block col-2 col-lg-3 text-left">Reinvest Bounty</div>
                                <div className="col col-5 col-md-2"></div>
                            </div>
                            {Object.keys(data.stakedPairList).map((factory: Factory, key: any) => (
                                <FactoryContext.Provider value={factory} key={key}>
                                    {data.stakedPairList[factory].map((pair: Address, key: any) => (
                                        <PairAddressContext.Provider value={pair} key={key}>
                                            <BountyRow />
                                        </PairAddressContext.Provider>
                                    ))}
                                </FactoryContext.Provider>
                            ))}
                        </Card>
                    </>
                )}
                {data.stakedPairList && data.stakedPairs === 0 && (
                    <div className="info">There is no bounty available yet</div>
                )}
            </Container>
        </View>
    );
}
